import React, { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';
import { SettingService } from 'features/userSettings';
import { timesheetsStore } from 'entities/timesheet';
import { DEFAULT_PINNABLE_COLUMNS, getColumns } from './TimesheetsTable.const';
import { TimesheetTableRow, CustomColumn } from './TimesheetsTable.types';
import { TimesheetCollapse } from './ui/TimesheetCollapse';
import { TimesheetsTableHeader } from './ui/TimesheetsTableHeader';
import styles from './TimesheetsLayout.module.scss';

const storatedColumnsVisibilityMap: Record<string, boolean> | null = SettingService.getLocalSetting(
  'timesheetColumnsVisibilityMap',
);
const storadedPinnedColumns: Record<string, boolean> | null = SettingService.getLocalSetting('timesheetPinnedColumns');

const TimesheetsLayout = (): React.ReactElement => {
  const { t } = useTranslation('Timesheet');

  const [pinnedColumns, setPinnedColumns] = useState<Record<string, boolean>>(
    storadedPinnedColumns ?? DEFAULT_PINNABLE_COLUMNS,
  );

  const allColumns: CustomColumn<TimesheetTableRow>[] = useMemo(
    () => getColumns(t, pinnedColumns),
    [t, pinnedColumns],
  ).sort((a, b) => {
    const aPinned = pinnedColumns[a.key as string];
    const bPinned = pinnedColumns[b.key as string];

    if (aPinned === bPinned) return 0;
    if (aPinned) return -1;
    return 1;
  });

  const columnOptions = useMemo(
    () =>
      allColumns.map(({ key, title }) => ({
        key,
        label: title,
      })),
    [allColumns],
  );
  const resetAll = useStore(timesheetsStore, (state) => state.resetAll);

  useEffect(() => {
    return () => resetAll();
  }, [resetAll]);

  const [columnsVisibilityMap, setColumnsVisibilityMap] = useState(
    storatedColumnsVisibilityMap ?? Object.fromEntries(columnOptions.map(({ key }) => [key, true])),
  );

  return (
    <div className={styles.container}>
      <TimesheetsTableHeader
        columns={columnOptions}
        columnsVisibilityMap={columnsVisibilityMap}
        setColumnsVisibilityMap={setColumnsVisibilityMap}
        pinnedColumns={pinnedColumns}
        setPinnedColumns={setPinnedColumns}
      />
      <TimesheetCollapse columnsVisibilityMap={columnsVisibilityMap} allColumns={allColumns} />
    </div>
  );
};

export default memo(TimesheetsLayout);
